import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder.tsx';
import { DealHealthScoreTableCell } from '@openx/components-deal/lib/components/DealHealthScore/DealHealthScoreTableCell.tsx';
import { AudienceCell } from '@openx/components-deal/lib/DealsList/parts/AudienceCell.tsx';
import { DealListDealTypePill } from '@openx/components-deal/lib/DealsList/parts/DealListDealTypePill.tsx';
import { buildBuyerFilterOptions } from '@openx/components-deal/lib/DealsList/parts/dealsMoreFiltersShared.ts';
import { MetricsCell } from '@openx/components-deal/lib/DealsList/parts/MetricsCell.tsx';
import { NameCellWithStatus } from '@openx/components-deal/lib/DealsList/parts/NameCellWithStatus.tsx';
import { ParticipantNamesCell } from '@openx/components-deal/lib/DealsList/parts/ParticipantNamesCell.tsx';
import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import type {
  DealAccount,
  DealsTableType,
  UseDealsTableFetchArgs,
} from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import { getDealStatusLabel } from '@openx/components-deal/lib/utils/getDealStatusLabel.ts';
import { useDealHealthScoreMock } from '@openx/components-deal/lib/utils/hooks/useDealHealthScoreMock.ts';
import type { FiltersConfig } from '@openx/types/chipFilters.ts';
import {
  type DealsTableFetchOptions,
  dealStatusForm,
  getOaDealStatus,
  type ListDealBase,
  type OAListDeal,
  pmpDealType,
} from '@openx/types/deal.ts';
import { organizationType } from '@openx/types/organization.ts';
import type { TableColumn } from '@openx/types/tableHelpers.ts';
import { toUTCDate } from '@openx/utils/lib/date.ts';
import { OPEN_AUDIENCE_ORG_ID, OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { useUsersList } from '../../../modules/deals/hooks/useOrganizationUsers.ts';
import { useBuyersFetch } from './participants/useBuyersFetch.ts';
import { useDemandPartnersFetch } from './participants/useDemandPartnersFetch.ts';

const isOAListDeal = (deal: ListDealBase | OAListDeal): deal is OAListDeal => {
  return 'exchange_uid' in deal;
};

export const useDealsTableFetch: DealProviderConfig['useDealsTableFetch'] = <T extends OAListDeal>(
  accounts?: DealAccount[],
  options?: DealsTableFetchOptions,
  args?: UseDealsTableFetchArgs
): DealsTableType<T> => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { getHealthScoreForDeal, loadHealthScoreDetails, reloadHealthScore } = useDealHealthScoreMock();

  const users = useUsersList();
  const isOxsSubOrg = session?.account?.name !== OPEN_AUDIENCE_ORG_NAME;
  const isOpenAudienceOrg = session?.exchange_session.account?.id === OPEN_AUDIENCE_ORG_ID;
  // EXCH-17901: Buyer orgs must not see Partner Revenue / DSP / Buyer columns
  // nor the matching Partner Revenue / DSP / Buyer filters. Detect from the OA
  // session's account organization type, not from ACL/role.
  const isBuyerOrg = session?.account?.organization_type === organizationType.Buyer;
  const { mapById: demandPartners = {} } = useDemandPartnersFetch();
  // Buyer orgs do not render DSP/Buyer filters; skip the buyers options fetch for them.
  const { mapById: buyersMap = {} } = useBuyersFetch(null, undefined, !isBuyerOrg);

  const columns: TableColumn<T>[] = useMemo(
    () => [
      {
        disabled: true,
        key: 'name',
        render: deal => {
          const normalizedStatusLabel = getDealStatusLabel(deal.status ?? '');
          const organizationName =
            isOpenAudienceOrg && isOAListDeal(deal) && deal.account ? deal.account.name : undefined;
          return (
            <NameCellWithStatus
              name={deal.name}
              id={deal.deal_id}
              pathTo={`/deals/${deal.uid}/details`}
              subRowLabel="ID:"
              deal_participants={deal.deal_participants}
              sync={deal.synchronized_with}
              dealStatusLabel={t(normalizedStatusLabel)}
              dealStatusKey={dealStatusForm[normalizedStatusLabel]}
              updateDealIdFilter={args?.updateDealIdFilter}
              listOfDealIdsToGroup={args?.dealIdsToGroup}
              isOxsSubOrg={isOxsSubOrg}
              organizationName={organizationName}
              dealError={deal.exchange_deal?.error}
              dealStatus={deal.status}
            />
          );
        },
        title: t('Name'),
        width: '21%',
        sticky: 'left',
        minWidth: '302px',
      },
      {
        align: 'left' as const,
        key: 'health_score',
        render: (deal: T) => (
          <DealHealthScoreTableCell
            deal={deal}
            getHealthScoreForDeal={getHealthScoreForDeal}
            loadHealthScoreDetails={loadHealthScoreDetails}
            reloadHealthScore={reloadHealthScore}
          />
        ),
        title: t('Health Score'),
        tooltipMessage: t(
          "Refreshed hourly deal's operational health score, based on its full funnel performance (current and historical), sustained trends, and the performance of deals with a similar setup."
        ),
        width: '14%',
        minWidth: '160px',
      },
      // Landing-backed metric columns. Rendered through MetricsCell so values, deltas,
      // and missing values follow the shared metric cell behavior.
      {
        align: 'left',
        key: 'spend',
        render: deal => <MetricsCell value={deal.spend} delta={deal.spend_delta} format="currency" />,
        title: t('Spend'),
        width: '10%',
        minWidth: '120px',
      },
      ...(!isBuyerOrg
        ? [
            {
              align: 'left' as const,
              key: 'partner_revenue',
              render: (deal: T) => (
                <MetricsCell value={deal.partner_revenue} delta={deal.partner_revenue_delta} format="currency" />
              ),
              title: t('Partner Revenue'),
              width: '14%',
              minWidth: '160px',
            },
          ]
        : []),
      {
        align: 'left',
        key: 'impressions',
        render: deal => <MetricsCell value={deal.impressions} delta={deal.impressions_delta} format="number" />,
        title: t('Impressions'),
        width: '10%',
        minWidth: '120px',
      },
      {
        align: 'left',
        key: 'cpm',
        render: deal => <MetricsCell value={deal.cpm} delta={deal.cpm_delta} format="cpm" />,
        title: t('CPM'),
        width: '12%',
        minWidth: '140px',
      },
      {
        key: 'audience',
        render: deal => {
          if (!isOAListDeal(deal)) return null;
          if (!deal?._package?.audience) return null;
          const { audience } = deal._package;

          return <AudienceCell audience={audience} />;
        },
        title: t('Audience'),
        width: '18%',
        minWidth: '270px',
      },
      {
        align: 'left',
        key: 'pmp_deal_type',
        title: t('Deal Type'),
        width: '10%',
        minWidth: '140px',
        render: deal => {
          if (!deal.pmp_deal_type) return <ValuePlaceholder />;
          const labelMap: Partial<Record<string, string>> = {
            [pmpDealType.Guaranteed]: t('Guaranteed'),
            [pmpDealType.PreferredDeal]: t('Preferred Deal'),
            [pmpDealType.PrivateAuction]: t('Private Auction'),
            [pmpDealType.WithinOpenAuction]: t('Within Open Auction'),
          };
          const label = labelMap[deal.pmp_deal_type] ?? deal.pmp_deal_type;
          return <DealListDealTypePill label={label} value={deal.pmp_deal_type} />;
        },
      },
      // EXCH-17901: DSP/Buyer participant columns are not exposed to Buyer orgs.
      // Columns are omitted (not `hide: true`) so they do not appear in
      // Customize columns and cannot be restored from `tableHeaderConfig-deals`.
      ...(!isBuyerOrg
        ? [
            {
              align: 'left' as const,
              key: 'dsp_names',
              title: t('DSP'),
              width: '10%',
              minWidth: '140px',
              render: deal => <ParticipantNamesCell names={deal.dsp_names ?? []} />,
            },
            {
              align: 'left' as const,
              key: 'buyer_names',
              title: t('Buyer'),
              width: '10%',
              minWidth: '140px',
              render: deal => <ParticipantNamesCell names={deal.buyer_names ?? []} />,
            },
          ]
        : []),
      {
        key: 'start_date',
        render: ({ start_date }) => toUTCDate(start_date),
        title: t('Start date'),
        width: '8%',
        minWidth: '120px',
      },
      {
        key: 'end_date',
        render: ({ end_date }) => (end_date ? toUTCDate(end_date) : <ValuePlaceholder />),
        title: t('End date'),
        width: '8%',
        minWidth: '120px',
      },
      {
        key: 'modified_date',
        isDate: true,
        title: t('Last Modified'),
        width: '8%',
        minWidth: '120px',
      },
      {
        hide: true,
        key: 'created_date',
        render: ({ created_date }) => (created_date ? toUTCDate(created_date) : <ValuePlaceholder />),
        title: t('Created'),
        width: '8%',
        minWidth: '120px',
      },
    ],
    [
      args,
      t,
      isOxsSubOrg,
      isOpenAudienceOrg,
      isBuyerOrg,
      getHealthScoreForDeal,
      loadHealthScoreDetails,
      reloadHealthScore,
    ]
  );

  const filtersConfig: FiltersConfig = useMemo((): FiltersConfig => {
    const filters: FiltersConfig = {
      exchange_deal_id: {
        inputType: 'text',
        multiple: true,
        name: t('Deal ID'),
      },
      status: {
        multiple: true,
        name: t('Status'),
        options: Object.values(getOaDealStatus(t)).map(value => {
          return { displayName: value, value };
        }),
      },
      createdBy: {
        multiple: true,
        name: t('Created by'),
        options: users?.map(user => {
          return {
            displayName: user.name,
            hidden: !!options?.selectedAccountId && options?.selectedAccountId !== user.id,
            value: user.id,
          };
        }),
      },
      myDeals: {
        name: t('Show my deals'),
        options: [{ value: users?.find(user => user.email === session?.email)?.id ?? '' }],
      },
    };

    // EXCH-17901: DSP / Buyer / Partner Revenue filters are hidden for Buyer orgs.
    // Mutate in insertion order so metric filters keep Spend → Partner Revenue → Impressions → CPM.
    if (!isBuyerOrg) {
      filters.demand_partner = {
        multiple: true,
        name: t('DSP'),
        options: Object.values(demandPartners).flatMap(demandPartner =>
          demandPartner?.id && demandPartner.name ? [{ displayName: demandPartner.name, value: demandPartner.id }] : []
        ),
      };
      filters.buyer_ids = {
        multiple: true,
        name: t('Buyer'),
        options: buildBuyerFilterOptions(buyersMap),
      };
    }

    filters.spend = {
      name: t('Spend'),
      numberFilter: { min: 0, max: 99999999999, maxFractionDigits: 2, currencySymbol: '$' },
    };

    if (!isBuyerOrg) {
      filters.partner_revenue = {
        name: t('Partner Revenue'),
        numberFilter: { min: 0, max: 99999999999, maxFractionDigits: 2, currencySymbol: '$' },
      };
    }

    filters.impressions = {
      name: t('Impressions'),
      numberFilter: { min: 0, max: 9999999999999, maxFractionDigits: 0 },
    };
    filters.cpm = {
      name: t('CPM'),
      numberFilter: { min: 0, max: 999999, maxFractionDigits: 2, currencySymbol: '$' },
    };

    filters.health_score = {
      name: t('Health Score'),
      numberFilter: { min: 0, max: 100, maxFractionDigits: 0 },
    };

    if (isOpenAudienceOrg) {
      filters.account = {
        multiple: true,
        name: t('Organization'),
        options: accounts?.map(item => ({ displayName: item.name, value: item.id })),
        loading: options?.accountsLoading,
      };
    }

    filters.pmp_deal_type = {
      multiple: true,
      name: t('Deal Type'),
      options: [
        { displayName: t('Private Auction'), value: pmpDealType.PrivateAuction },
        { displayName: t('Within Open Auction'), value: pmpDealType.WithinOpenAuction },
      ],
    };

    return filters;
  }, [
    t,
    accounts,
    buyersMap,
    demandPartners,
    options?.accountsLoading,
    options?.selectedAccountId,
    session,
    users,
    isOpenAudienceOrg,
    isBuyerOrg,
  ]);

  return { columns, filtersConfig };
};
